// =============================================================================
// clientPoUpload.ts — small helper for the Client PO upload flow.
//
// 3-step pattern (mirrors hopUpload + deliverablesUpload):
//   1. POST /sign-upload to api-gateway → presigned PUT URL + object_key
//      under pos/{quote_id}/.
//   2. PUT bytes directly to R2 (zero data through the Worker).
//   3. POST /quotes/save-po → worker writes the files row + updates the
//      quotes row with the PO metadata + file_id.
// =============================================================================

import { apiFetch } from "@/lib/supabase";

export interface ClientPoUploadArgs {
  quoteId:        string;
  poNumber:       string;
  poAmountExGst:  number;
  poDate:         string;          // ISO yyyy-mm-dd
  file:           File;
  notes?:         string | null;
}

export interface ClientPoUploadResult {
  file_id: string;
}

export async function uploadClientPo(args: ClientPoUploadArgs): Promise<ClientPoUploadResult> {
  const { quoteId, poNumber, poAmountExGst, poDate, file, notes } = args;

  // 1. Build the object key + ask for a presigned PUT URL.
  const safeName = file.name.replace(/[^A-Za-z0-9._-]+/g, "_").slice(0, 80);
  const ts        = Date.now();
  const objectKey = `pos/${quoteId}/${ts}-${safeName}`;

  const signResp = await apiFetch("/sign-upload", {
    method: "POST",
    body:   JSON.stringify({
      bucket:       "coretel-prod",
      object_key:   objectKey,
      content_type: file.type || "application/pdf",
    }),
  });
  if (!signResp.ok) {
    const e = await signResp.json().catch(() => ({}));
    throw new Error(`sign-upload failed: ${(e as any)?.error ?? signResp.status}`);
  }
  const { upload_url } = await signResp.json() as { upload_url: string };

  // 2. PUT bytes directly to R2.
  const putResp = await fetch(upload_url, {
    method:  "PUT",
    headers: { "Content-Type": file.type || "application/pdf" },
    body:    file,
  });
  if (!putResp.ok) {
    throw new Error(`R2 PUT failed: ${putResp.status} ${putResp.statusText}`);
  }

  // 3. Tell the worker to record metadata.
  const saveResp = await apiFetch("/quotes/save-po", {
    method: "POST",
    body:   JSON.stringify({
      quote_id:         quoteId,
      po_number:        poNumber,
      po_amount_ex_gst: poAmountExGst,
      po_date:          poDate,
      object_key:       objectKey,
      file_name:        file.name,
      file_size_bytes:  file.size,
      mime_type:        file.type || "application/pdf",
      notes:            notes ?? null,
    }),
  });
  if (!saveResp.ok) {
    const e = await saveResp.json().catch(() => ({}));
    throw new Error(`save-po failed: ${(e as any)?.error ?? saveResp.status} ${(e as any)?.detail ?? ""}`);
  }
  const { file_id } = await saveResp.json() as { file_id: string };
  return { file_id };
}

// -----------------------------------------------------------------------------
// Helper: signed download URL for an existing PO PDF (uses /sign-download).
// -----------------------------------------------------------------------------
export async function getClientPoDownloadUrl(objectKey: string): Promise<string> {
  const r = await apiFetch("/sign-download", {
    method: "POST",
    body:   JSON.stringify({
      bucket:     "coretel-prod",
      object_key: objectKey,
    }),
  });
  if (!r.ok) {
    const e = await r.json().catch(() => ({}));
    throw new Error(`sign-download failed: ${(e as any)?.error ?? r.status}`);
  }
  const { download_url } = await r.json() as { download_url: string };
  return download_url;
}
